import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Table,
  UncontrolledTooltip,
} from "reactstrap";

import {
  downloadTaxDocument,
  getPaddleInvoiceUrl,
  getSubscriptionInvoices,
  SaasBillingInvoice,
  SaasBillingInvoiceTaxDocument,
} from "../../../services/subscriptionService";

const resolveIntlLocale = (language: string): string =>
  language === "sp" ? "es" : language;

const formatMoney = (cents: number, currency: string, language: string): string =>
  new Intl.NumberFormat(resolveIntlLocale(language), {
    style: "currency",
    currency,
  }).format(cents / 100);

const formatPeriod = (start: string, end: string, language: string): string => {
  const formatter = new Intl.DateTimeFormat(resolveIntlLocale(language), {
    dateStyle: "medium",
  });

  return `${formatter.format(new Date(start))} – ${formatter.format(new Date(end))}`;
};

const paymentStatusColor = (status: string): string => {
  switch (status) {
    case "paid":
      return "success";
    case "pending":
      return "warning";
    case "failed":
      return "danger";
    default:
      return "secondary";
  }
};

const taxStatusColor = (status: string): string => {
  switch (status) {
    case "issued":
      return "success";
    case "pending":
      return "warning";
    case "failed":
      return "danger";
    default:
      return "secondary";
  }
};

const resolveTaxDocumentTooltip = (
  taxDocument: SaasBillingInvoiceTaxDocument,
  t: (key: string) => string
): string => {
  const apiMessage = taxDocument.error_message?.trim();

  if (apiMessage) {
    return apiMessage;
  }

  if (taxDocument.document_type === "nfse_export") {
    return t("settings.subscription.invoices.taxStatusFailedTooltipExport");
  }

  return t("settings.subscription.invoices.taxStatusFailedTooltip");
};

const resolveTaxStatusLabel = (
  taxDocument: SaasBillingInvoiceTaxDocument,
  t: (key: string, options?: { defaultValue?: string }) => string
): string => {
  const isExport = taxDocument.document_type === "nfse_export";
  const exportKey = `settings.subscription.invoices.taxStatus.export.${taxDocument.status}`;
  const defaultKey = `settings.subscription.invoices.taxStatus.${taxDocument.status}`;

  if (isExport) {
    return t(exportKey, { defaultValue: t(defaultKey, { defaultValue: taxDocument.status }) });
  }

  return t(defaultKey, { defaultValue: taxDocument.status });
};

const isExportTaxDocument = (taxDocument: SaasBillingInvoiceTaxDocument): boolean =>
  taxDocument.document_type === "nfse_export";

const canOfferPaddleInvoice = (invoice: SaasBillingInvoice): boolean => {
  if (invoice.gateway_slug !== "paddle" || invoice.amount_cents <= 0) {
    return false;
  }

  const storedUrl = invoice.paddle_invoice_url?.trim() || "";
  const externalId = invoice.external_invoice_id?.trim() || "";

  return storedUrl !== "" || externalId.startsWith("txn_");
};

const openExternalUrl = (url: string): Window | null =>
  window.open(url, "_blank", "noopener,noreferrer");

const InvoicesTableSkeleton: React.FC = () => (
  <div className="table-responsive placeholder-glow">
    <Table className="align-middle mb-0">
      <thead className="table-light">
        <tr>
          {[0, 1, 2, 3, 4].map((key) => (
            <th key={key}>
              <span className="placeholder col-8" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[0, 1, 2].map((row) => (
          <tr key={row}>
            {[0, 1, 2, 3, 4].map((col) => (
              <td key={col}>
                <span className="placeholder col-10" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  </div>
);

interface InvoicesSectionProps {
  onSubscribe?: () => void;
}

const InvoicesSection: React.FC<InvoicesSectionProps> = ({ onSubscribe }) => {
  const { t, i18n } = useTranslation();
  const [invoices, setInvoices] = useState<SaasBillingInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingTaxDocumentId, setDownloadingTaxDocumentId] = useState<number | null>(
    null
  );
  const [openingPaddleInvoiceId, setOpeningPaddleInvoiceId] = useState<number | null>(null);

  const loadInvoices = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getSubscriptionInvoices();
      setInvoices(response.data ?? []);
    } catch {
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const handleDownloadTaxDocument = async (taxDocumentId: number): Promise<void> => {
    setDownloadingTaxDocumentId(taxDocumentId);

    try {
      await downloadTaxDocument(taxDocumentId);
    } catch (error: any) {
      toast.error(
        error?.data || t("settings.subscription.invoices.downloadError")
      );
    } finally {
      setDownloadingTaxDocumentId(null);
    }
  };

  const handleOpenPaddleInvoice = async (invoice: SaasBillingInvoice): Promise<void> => {
    setOpeningPaddleInvoiceId(invoice.id);

    try {
      const response = await getPaddleInvoiceUrl(invoice.id);
      const url = response.data?.paddle_invoice_url?.trim() || "";

      if (!url) {
        toast.error(t("settings.subscription.invoices.paddleInvoiceError"));
        return;
      }

      const popup = openExternalUrl(url);

      if (popup == null) {
        toast.error(t("settings.subscription.invoices.paddleInvoicePopupBlocked"));
        return;
      }
    } catch {
      const fallback = invoice.paddle_invoice_url?.trim() || "";

      if (fallback) {
        const popup = openExternalUrl(fallback);

        if (popup == null) {
          toast.error(t("settings.subscription.invoices.paddleInvoicePopupBlocked"));
        }

        return;
      }

      toast.error(t("settings.subscription.invoices.paddleInvoiceError"));
    } finally {
      setOpeningPaddleInvoiceId(null);
    }
  };

  return (
    <Card className="mt-4">
      <CardHeader>
        <h5 className="card-title mb-0">{t("settings.subscription.invoices.title")}</h5>
      </CardHeader>
      <CardBody>
        {loading ? (
          <InvoicesTableSkeleton />
        ) : invoices.length === 0 ? (
          <div className="text-center py-4">
            <i className="ri-file-list-3-line display-4 text-muted" aria-hidden="true" />
            <p className="text-muted mt-3 mb-3">{t("settings.subscription.invoices.empty")}</p>
            {onSubscribe ? (
              <Button color="primary" onClick={onSubscribe}>
                {t("settings.subscription.invoices.subscribeCta")}
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="table-responsive">
            <Table className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>{t("settings.subscription.invoices.columns.period")}</th>
                  <th>{t("settings.subscription.invoices.columns.amount")}</th>
                  <th>{t("settings.subscription.invoices.columns.payment")}</th>
                  <th>{t("settings.subscription.invoices.columns.tax")}</th>
                  <th>{t("settings.subscription.invoices.columns.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => {
                  const taxDocument = invoice.tax_document;
                  const canDownload = taxDocument?.status === "issued";
                  const isDownloading =
                    taxDocument != null &&
                    downloadingTaxDocumentId === taxDocument.id;
                  const hasPaddleInvoice = canOfferPaddleInvoice(invoice);
                  const isOpeningPaddle = openingPaddleInvoiceId === invoice.id;
                  const taxBadgeId = `tax-status-${invoice.id}`;
                  const showTaxFailedTooltip = taxDocument?.status === "failed";
                  const isExportTax = taxDocument != null && isExportTaxDocument(taxDocument);
                  const hasActions = (canDownload && taxDocument) || hasPaddleInvoice;

                  return (
                    <tr key={invoice.id}>
                      <td>
                        {formatPeriod(
                          invoice.billing_period_start,
                          invoice.billing_period_end,
                          i18n.language
                        )}
                      </td>
                      <td>
                        {formatMoney(invoice.amount_cents, invoice.currency, i18n.language)}
                      </td>
                      <td>
                        <Badge
                          color={paymentStatusColor(invoice.payment_status)}
                          className="text-uppercase"
                        >
                          {t(`settings.subscription.invoices.paymentStatus.${invoice.payment_status}`, {
                            defaultValue: invoice.payment_status,
                          })}
                        </Badge>
                      </td>
                      <td>
                        {taxDocument ? (
                          <div className="d-flex flex-wrap align-items-center gap-1">
                            {isExportTax ? (
                              <Badge color="info" className="text-uppercase">
                                {t("settings.subscription.invoices.taxType.export")}
                              </Badge>
                            ) : null}
                            <Badge
                              id={showTaxFailedTooltip ? taxBadgeId : undefined}
                              color={taxStatusColor(taxDocument.status)}
                              className="text-uppercase"
                              style={showTaxFailedTooltip ? { cursor: "help" } : undefined}
                            >
                              {resolveTaxStatusLabel(taxDocument, t)}
                            </Badge>
                            {showTaxFailedTooltip ? (
                              <UncontrolledTooltip placement="top" target={taxBadgeId}>
                                {resolveTaxDocumentTooltip(taxDocument, t)}
                              </UncontrolledTooltip>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {hasActions ? (
                          <div className="d-flex flex-wrap gap-2">
                            {canDownload && taxDocument ? (
                              <Button
                                color="soft-primary"
                                size="sm"
                                outline
                                disabled={isDownloading}
                                onClick={() => handleDownloadTaxDocument(taxDocument.id)}
                              >
                                {isDownloading ? (
                                  <Spinner size="sm" />
                                ) : (
                                  t(
                                    isExportTax
                                      ? "settings.subscription.invoices.downloadExportPdf"
                                      : "settings.subscription.invoices.downloadPdf"
                                  )
                                )}
                              </Button>
                            ) : null}
                            {hasPaddleInvoice ? (
                              <Button
                                color="soft-secondary"
                                size="sm"
                                outline
                                disabled={isOpeningPaddle}
                                onClick={() => handleOpenPaddleInvoice(invoice)}
                              >
                                {isOpeningPaddle ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <>
                                    <i className="ri-external-link-line me-1" aria-hidden="true" />
                                    {t("settings.subscription.invoices.paddleInvoice")}
                                  </>
                                )}
                              </Button>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default InvoicesSection;
